// Centralized date utilities and constants

export const MONTH_NAMES = {
  short: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
  full: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
} as const;

export const DAY_NAMES = {
  short: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  full: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
} as const;

const shouldTitleCaseLocale = (locale?: string) => {
  if (!locale) {
    return false;
  }
  const normalized = locale.split("-")[0].toLowerCase();
  return ["sv", "da", "no", "nb", "nn"].includes(normalized);
};

const isSeparator = (value: string) => /[\s\u2013\u2014.,/()\-]/.test(value);

const titleCaseLocale = (value: string, locale: string) => {
  let result = "";
  let shouldCapitalize = true;
  for (const char of value) {
    const hasCase =
      char.toLocaleLowerCase(locale) !== char.toLocaleUpperCase(locale);
    if (shouldCapitalize && hasCase) {
      result += char.toLocaleUpperCase(locale);
    } else {
      result += char;
    }
    shouldCapitalize = isSeparator(char);
  }
  return result;
};

/**
 * Format a date string (YYYY-MM-DD) to a readable format
 * @param dateStr - Date string in YYYY-MM-DD format
 * @param monthFormat - 'short' or 'full' month names
 * @param locale - Optional locale for localized formatting
 * @returns Formatted date string
 */
export function formatDate(
  dateInput: string | Date,
  monthFormat: "short" | "full" = "full",
  locale?: string,
): string {
  const resolveDate = (value: string | Date): Date | null => {
    if (value instanceof Date) {
      return value;
    }

    const raw = value.trim();
    if (!raw) {
      return null;
    }

    const datePart = raw.includes("T")
      ? raw.split("T")[0]
      : raw.includes(" ")
        ? raw.split(" ")[0]
        : raw;
    const parts = datePart.split("-");

    if (parts.length >= 3 && parts[0].length === 4) {
      const year = parseInt(parts[0], 10);
      const monthIndex = parseInt(parts[1], 10) - 1;
      const dayNum = parseInt(parts[2], 10);
      if (
        Number.isFinite(year) &&
        Number.isFinite(monthIndex) &&
        Number.isFinite(dayNum)
      ) {
        return new Date(year, monthIndex, dayNum);
      }
    }

    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const date = resolveDate(dateInput);
  if (!date) {
    return typeof dateInput === "string" ? dateInput : "";
  }

  if (locale) {
    try {
      const options: Intl.DateTimeFormatOptions =
        monthFormat === "short"
          ? { month: "short", day: "numeric" }
          : {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric",
            };
      const formatted = new Intl.DateTimeFormat(locale, options).format(date);
      return shouldTitleCaseLocale(locale)
        ? titleCaseLocale(formatted, locale)
        : formatted;
    } catch (error) {
      console.warn("Failed to format date for locale", locale, error);
    }
  }

  const dayNum = date.getDate();
  const year = date.getFullYear();
  const monthName = MONTH_NAMES[monthFormat][date.getMonth()];

  if (monthFormat === "short") {
    return `${monthName} ${dayNum}`;
  }

  // For full format, include day of week
  const dayOfWeek = DAY_NAMES.full[date.getDay()];

  return `${dayOfWeek}, ${monthName} ${dayNum}, ${year}`;
}

/**
 * Get month name by index (0-based)
 * @param monthIndex - Month index (0-11)
 * @param format - 'short' or 'full'
 * @returns Month name
 */
export function getMonthName(
  monthIndex: number,
  format: "short" | "full" = "full",
): string {
  return MONTH_NAMES[format][monthIndex];
}

/**
 * Get day name by index (0-based, 0 = Sunday)
 * @param dayIndex - Day index (0-6)
 * @param format - 'short' or 'full'
 * @returns Day name
 */
export function getDayName(
  dayIndex: number,
  format: "short" | "full" = "full",
): string {
  return DAY_NAMES[format][dayIndex];
}
